/**
 * Which machine runs your calls and transcribes Mac-path audio: an online Mac (the call agent
 * runs on your subscription there and whisper uses Metal). Pure selector + hook.
 */

import { useMachines } from '../queries.js'
import type { Machine } from '../types.js'

export interface CallMac {
  id: string
  /** The display name ("Areks MacBook Pro"), never the raw hostname. */
  name: string
  online?: boolean
}

/** "Areks-Macbook-Pro.local" → "Areks MacBook Pro"; names people chose stay as they are. */
export function macDisplayName(host: string): string {
  const bare = host.replace(/\.(local|lan|home|internal)$/i, '')
  if (bare.includes(' ')) return bare
  return bare
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) =>
      /^macbook$/i.test(w) ? 'MacBook' : /^imac$/i.test(w) ? 'iMac' : /^mac$/i.test(w) ? 'Mac' : w,
    )
    .join(' ')
}

export function pickCallMac(machines: readonly Machine[] | undefined): CallMac | undefined {
  const macs = (machines ?? []).filter(
    (m) => m.online && (m.platform === 'mac' || m.platform === 'darwin'),
  )
  const m = macs.find((x) => x.awake !== false) ?? macs[0]
  return m ? { id: m.id, name: macDisplayName(m.name), online: true } : undefined
}

/** The Mac a call would use, online or not (for "Mac Studio is offline"). */
export function anyCallMac(machines: readonly Machine[] | undefined): CallMac | undefined {
  const online = pickCallMac(machines)
  if (online) return online
  const m = (machines ?? []).find((x) => x.platform === 'mac' || x.platform === 'darwin')
  return m ? { id: m.id, name: macDisplayName(m.name), online: false } : undefined
}

export function useCallMac(): CallMac | undefined {
  return pickCallMac(useMachines().data)
}
